import { useState, useEffect } from "react";
import { 
  Globe,
  Save,
  Eye,
  EyeOff,
  Loader2,
  Check,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";
import NexusHomeEditor from "./NexusHomeEditor";

const SECTIONS = [
  { key: "hero", label: "Hero", icon: "🏠" },
  { key: "cases", label: "Kundcase", icon: "🏆" },
  { key: "new_or_rebuild", label: "Ny eller bygga om?", icon: "🔀" },
  { key: "why_elido", label: "Varför Elido", icon: "✨" },
  { key: "pricing_hemsidor", label: "Priser (hemsidor)", icon: "💰" },
  { key: "process", label: "Process", icon: "📋" },
  { key: "personal", label: "Personlig kontakt", icon: "👥" },
  { key: "faq", label: "FAQ", icon: "❓" },
  { key: "services", label: "Tjänster", icon: "🛠️" },
  { key: "more_than_websites", label: "Mer än hemsidor (utfasad)", icon: "🗄️" },
  { key: "seo_links", label: "SEO-länkar", icon: "🔗" },
  { key: "cta", label: "CTA-sektion", icon: "📢" },
  { key: "contact", label: "Kontakt", icon: "📧" },
  { key: "footer", label: "Footer", icon: "📝" }
];

export default function WebsiteContentEditor() {
  const { getAuthHeaders } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [expandedSection, setExpandedSection] = useState("hero");
  // Top-level tab: which page do we edit? "nexus" = new flagship homepage,
  // "hemsidor" = legacy /hemsidor (SEO) page.
  const [activeTab, setActiveTab] = useState("nexus");

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const contentRes = await fetch(`${API_URL}/api/website/content`, { 
        credentials: 'include' 
      });
      const contentData = await contentRes.json();
      setContent(contentData.content);
    } catch (error) {
      console.error("Failed to load content:", error);
      toast.error("Kunde inte ladda innehåll");
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (sectionKey) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/website/content`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          section: sectionKey,
          content: content[sectionKey]
        })
      });

      if (response.ok) {
        toast.success("Sparat!");
      } else {
        throw new Error("Save failed");
      }
    } catch (error) {
      toast.error("Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibility = async (sectionKey) => {
    const newVisibility = !content[sectionKey]?.visible;
    
    setContent(prev => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], visible: newVisibility }
    }));

    try {
      await fetch(`${API_URL}/api/website/section-visibility`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          section: sectionKey,
          visible: newVisibility
        })
      });
      toast.success(newVisibility ? "Sektion visas nu" : "Sektion dold");
    } catch (error) {
      toast.error("Kunde inte uppdatera synlighet");
    }
  };

  const seedSeoLinks = async () => {
    if (!window.confirm("Detta importerar SEO-länkarna (sektionen 'Populära tjänster') till startsidan. Vill du fortsätta?")) {
      return;
    }
    
    setSeeding(true);
    try {
      const response = await fetch(`${API_URL}/api/website/seed-seo-links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.created) {
          toast.success("SEO-länkar importerade!");
          loadContent();
        } else {
          toast.info(data.message || "SEO-länkar finns redan");
        }
      } else {
        throw new Error("Seed failed");
      }
    } catch (error) {
      console.error("Seed error:", error);
      toast.error("Kunde inte importera SEO-länkar");
    } finally {
      setSeeding(false);
    }
  };

  const updateField = (sectionKey, field, value) => {
    setContent(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: value
      }
    }));
  };

  const updateNestedField = (sectionKey, field, subfield, value) => {
    setContent(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: {
          ...prev[sectionKey]?.[field],
          [subfield]: value
        }
      }
    }));
  };

  const updateArrayItem = (sectionKey, field, index, itemField, value) => {
    setContent(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: prev[sectionKey]?.[field]?.map((item, i) => 
          i === index ? { ...item, [itemField]: value } : item
        )
      }
    }));
  };

  const addArrayItem = (sectionKey, field, newItem) => {
    setContent(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: [...(prev[sectionKey]?.[field] || []), newItem]
      }
    }));
  };

  const removeArrayItem = (sectionKey, field, index) => {
    setContent(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [field]: prev[sectionKey]?.[field]?.filter((_, i) => i !== index)
      }
    }));
  };

  if (loading && activeTab === "hemsidor") {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top-level page tabs: which website do we edit? */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Globe className="w-7 h-7 text-[#255E69]" />
            Webbplatsinnehåll
          </h1>
          <p className="mt-1 text-gray-500">
            Hantera startsidan och hemsidor (SEO) — allt innehåll lagras i databasen.
          </p>
        </div>
      </div>
      <div className="flex gap-2 border-b border-gray-200" data-testid="website-page-tabs">
        <button
          type="button"
          onClick={() => setActiveTab("nexus")}
          className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === "nexus"
              ? "border-[#255E69] text-[#255E69]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          data-testid="website-tab-nexus"
        >
          Startsida
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("hemsidor")}
          className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
            activeTab === "hemsidor"
              ? "border-[#255E69] text-[#255E69]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          data-testid="website-tab-hemsidor"
        >
          Hemsidor (SEO)
        </button>
      </div>

      {activeTab === "nexus" ? <NexusHomeEditor /> : (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Hemsidor (SEO) — /hemsidor
          </h2>
          <p className="mt-1 text-gray-500 text-sm">
            Redigera texter, sektioner och priser på SEO-startsidan (/hemsidor).
          </p>
        </div>
        <a 
          href="/hemsidor" 
          target="_blank"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
        >
          <Eye className="w-4 h-4" />
          Förhandsgranska
        </a>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <div className="px-4 py-3 text-sm font-medium border-b-2 border-[#255E69] text-[#255E69]">
          Innehåll & Sektioner
        </div>
        <a
          href="/admin/pricing"
          className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-2"
        >
          Priser
          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">→ Prissättning</span>
        </a>
      </div>

      {/* Content Sections */}
      <div className="space-y-4">
        {SECTIONS.map((section) => {
            const sectionData = content?.[section.key] || { };
            const isExpanded = expandedSection === section.key;
            const isVisible = sectionData.visible !== false;

            return (
              <div 
                key={section.key}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                {/* Section Header */}
                <div 
                  className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedSection(isExpanded ? null : section.key)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{section.icon}</span>
                    <span className="font-medium text-gray-900">{section.label}</span>
                    {!isVisible && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                        Dold
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleVisibility(section.key); }}
                      className={`p-2 rounded-lg transition-colors ${
                        isVisible ? "text-green-600 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100"
                      }`}
                      title={isVisible ? "Dölj sektion" : "Visa sektion"}
                    >
                      {isVisible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Section Content */}
                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
                    {/* Hero Section */}
                    {section.key === "hero" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Etikett (eyebrow)</label>
                          <input
                            type="text"
                            value={sectionData.eyebrow || ""}
                            onChange={(e) => updateField("hero", "eyebrow", e.target.value)}
                            placeholder="T.ex. Hemsidor för företag"
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Rubrik</label>
                          <input
                            type="text"
                            value={sectionData.headline || ""}
                            onChange={(e) => updateField("hero", "headline", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Underrubrik</label>
                          <textarea
                            value={sectionData.subheadline || ""}
                            onChange={(e) => updateField("hero", "subheadline", e.target.value)}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hero-bild (URL/media)</label>
                            <input
                              type="text"
                              value={sectionData.hero_image || ""}
                              onChange={(e) => updateField("hero", "hero_image", e.target.value)}
                              placeholder="/img/hemsidor/case-nordenstad-1280.webp"
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hero-bild alt-text</label>
                            <input
                              type="text"
                              value={sectionData.hero_image_alt || ""}
                              onChange={(e) => updateField("hero", "hero_image_alt", e.target.value)}
                              placeholder="Beskrivande alt-text"
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Primär CTA text</label>
                            <input
                              type="text"
                              value={sectionData.cta_primary?.text || ""}
                              onChange={(e) => updateNestedField("hero", "cta_primary", "text", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Primär CTA länk</label>
                            <input
                              type="text"
                              value={sectionData.cta_primary?.link || ""}
                              onChange={(e) => updateNestedField("hero", "cta_primary", "link", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sekundär CTA text</label>
                            <input
                              type="text"
                              value={sectionData.cta_secondary?.text || ""}
                              onChange={(e) => updateNestedField("hero", "cta_secondary", "text", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sekundär CTA länk</label>
                            <input
                              type="text"
                              value={sectionData.cta_secondary?.link || ""}
                              onChange={(e) => updateNestedField("hero", "cta_secondary", "link", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Cases Section */}
                    {section.key === "cases" && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Etikett</label>
                            <input type="text" value={sectionData.eyebrow || ""} onChange={(e) => updateField("cases", "eyebrow", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                            <input type="text" value={sectionData.title || ""} onChange={(e) => updateField("cases", "title", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Ingress</label>
                          <textarea value={sectionData.subtitle || ""} onChange={(e) => updateField("cases", "subtitle", e.target.value)} rows={2} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Utvalda case</label>
                          <div className="space-y-3">
                            {(sectionData.items || []).map((item, i) => (
                              <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-semibold text-gray-500">Case {i + 1}</span>
                                  <button onClick={() => removeArrayItem("cases", "items", i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <input type="text" value={item.name || ""} onChange={(e) => updateArrayItem("cases", "items", i, "name", e.target.value)} placeholder="Namn" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                  <input type="text" value={item.category || ""} onChange={(e) => updateArrayItem("cases", "items", i, "category", e.target.value)} placeholder="Kategori" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <textarea value={item.description || ""} onChange={(e) => updateArrayItem("cases", "items", i, "description", e.target.value)} placeholder="Beskrivning" rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                <input type="text" value={item.image_url || ""} onChange={(e) => updateArrayItem("cases", "items", i, "image_url", e.target.value)} placeholder="Bild-URL" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                <input type="text" value={item.url || ""} onChange={(e) => updateArrayItem("cases", "items", i, "url", e.target.value)} placeholder="Länk till hemsidan" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                              </div>
                            ))}
                            <button onClick={() => addArrayItem("cases", "items", { name: "", category: "", description: "", image_url: "", url: "" })} className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"><Plus className="w-4 h-4" />Lägg till case</button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* New or rebuild Section */}
                    {section.key === "new_or_rebuild" && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Etikett</label>
                            <input type="text" value={sectionData.eyebrow || ""} onChange={(e) => updateField("new_or_rebuild", "eyebrow", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                            <input type="text" value={sectionData.title || ""} onChange={(e) => updateField("new_or_rebuild", "title", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Ingress</label>
                          <textarea value={sectionData.intro || ""} onChange={(e) => updateField("new_or_rebuild", "intro", e.target.value)} rows={2} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Alternativ</label>
                          <div className="space-y-3">
                            {(sectionData.options || []).map((item, i) => (
                              <div key={i} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                                <div className="flex-1 space-y-2">
                                  <input type="text" value={item.title || ""} onChange={(e) => updateArrayItem("new_or_rebuild", "options", i, "title", e.target.value)} placeholder="Titel" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                  <textarea value={item.text || ""} onChange={(e) => updateArrayItem("new_or_rebuild", "options", i, "text", e.target.value)} placeholder="Text" rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <button onClick={() => removeArrayItem("new_or_rebuild", "options", i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            ))}
                            <button onClick={() => addArrayItem("new_or_rebuild", "options", { title: "", text: "" })} className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"><Plus className="w-4 h-4" />Lägg till alternativ</button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Pricing (hemsidor) Section */}
                    {section.key === "pricing_hemsidor" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input type="text" value={sectionData.title || ""} onChange={(e) => updateField("pricing_hemsidor", "title", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Ingress</label>
                          <textarea value={sectionData.subtitle || ""} onChange={(e) => updateField("pricing_hemsidor", "subtitle", e.target.value)} rows={2} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Prisnivåer</label>
                          <div className="space-y-4">
                            {(sectionData.tiers || []).map((tier, i) => (
                              <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-semibold text-gray-500">Nivå {i + 1}</span>
                                  <div className="flex items-center gap-3">
                                    <label className="flex items-center gap-1.5 text-xs text-gray-600">
                                      <input type="checkbox" checked={!!tier.highlighted} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "highlighted", e.target.checked)} />
                                      Markerad
                                    </label>
                                    <button onClick={() => removeArrayItem("pricing_hemsidor", "tiers", i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <input type="text" value={tier.name || ""} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "name", e.target.value)} placeholder="Namn" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                  <input type="text" value={tier.price_from || ""} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "price_from", e.target.value)} placeholder="Pris från" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                  <input type="text" value={tier.period || ""} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "period", e.target.value)} placeholder="Enhet (kr / kr/mån)" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <textarea value={tier.description || ""} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "description", e.target.value)} placeholder="Beskrivning" rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                <textarea value={(tier.features || []).join("\n")} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "features", e.target.value.split("\n").filter(Boolean))} placeholder="Vad ingår (en rad per punkt)" rows={4} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                <div className="grid grid-cols-2 gap-2">
                                  <input type="text" value={tier.cta_text || ""} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "cta_text", e.target.value)} placeholder="CTA text" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                  <input type="text" value={tier.cta_link || ""} onChange={(e) => updateArrayItem("pricing_hemsidor", "tiers", i, "cta_link", e.target.value)} placeholder="CTA länk" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                              </div>
                            ))}
                            <button onClick={() => addArrayItem("pricing_hemsidor", "tiers", { name: "", price_from: "", period: "kr", description: "", features: [], cta_text: "Få ett förslag", cta_link: "#kontakt", highlighted: false })} className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"><Plus className="w-4 h-4" />Lägg till prisnivå</button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Fotnot</label>
                          <input type="text" value={sectionData.footnote || ""} onChange={(e) => updateField("pricing_hemsidor", "footnote", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                      </>
                    )}

                    {/* Personal Section */}
                    {section.key === "personal" && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Etikett</label>
                            <input type="text" value={sectionData.eyebrow || ""} onChange={(e) => updateField("personal", "eyebrow", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                            <input type="text" value={sectionData.title || ""} onChange={(e) => updateField("personal", "title", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Beskrivning</label>
                          <textarea value={sectionData.description || ""} onChange={(e) => updateField("personal", "description", e.target.value)} rows={2} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Personer</label>
                          <div className="space-y-3">
                            {(sectionData.people || []).map((p, i) => (
                              <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-semibold text-gray-500">Person {i + 1}</span>
                                  <button onClick={() => removeArrayItem("personal", "people", i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <input type="text" value={p.name || ""} onChange={(e) => updateArrayItem("personal", "people", i, "name", e.target.value)} placeholder="Namn" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                  <input type="text" value={p.role || ""} onChange={(e) => updateArrayItem("personal", "people", i, "role", e.target.value)} placeholder="Roll/företag" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                                <input type="text" value={p.image_url || ""} onChange={(e) => updateArrayItem("personal", "people", i, "image_url", e.target.value)} placeholder="Bild-URL" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                <div className="grid grid-cols-2 gap-2">
                                  <input type="text" value={p.phone || ""} onChange={(e) => updateArrayItem("personal", "people", i, "phone", e.target.value)} placeholder="Telefon" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                  <input type="text" value={p.email || ""} onChange={(e) => updateArrayItem("personal", "people", i, "email", e.target.value)} placeholder="E-post" className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                </div>
                              </div>
                            ))}
                            <button onClick={() => addArrayItem("personal", "people", { name: "", role: "Elido AB", image_url: "", phone: "", email: "" })} className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"><Plus className="w-4 h-4" />Lägg till person</button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* FAQ Section */}
                    {section.key === "faq" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input type="text" value={sectionData.title || ""} onChange={(e) => updateField("faq", "title", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Ingress</label>
                          <input type="text" value={sectionData.subtitle || ""} onChange={(e) => updateField("faq", "subtitle", e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Frågor</label>
                          <div className="space-y-3">
                            {(sectionData.items || []).map((item, i) => (
                              <div key={i} className="p-4 bg-gray-50 rounded-lg space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-semibold text-gray-500">Fråga {i + 1}</span>
                                  <button onClick={() => removeArrayItem("faq", "items", i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                                </div>
                                <input type="text" value={item.question || ""} onChange={(e) => updateArrayItem("faq", "items", i, "question", e.target.value)} placeholder="Fråga" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                <textarea value={item.answer || ""} onChange={(e) => updateArrayItem("faq", "items", i, "answer", e.target.value)} placeholder="Svar" rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                <input type="number" value={item.order ?? (i + 1)} onChange={(e) => updateArrayItem("faq", "items", i, "order", parseInt(e.target.value) || 0)} placeholder="Sortering" className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                              </div>
                            ))}
                            <button onClick={() => addArrayItem("faq", "items", { question: "", answer: "", order: (sectionData.items || []).length + 1 })} className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"><Plus className="w-4 h-4" />Lägg till fråga</button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Services Section */}
                    {section.key === "services" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input
                            type="text"
                            value={sectionData.title || ""}
                            onChange={(e) => updateField("services", "title", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Undertitel</label>
                          <input
                            type="text"
                            value={sectionData.subtitle || ""}
                            onChange={(e) => updateField("services", "subtitle", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Tjänster</label>
                          <div className="space-y-3">
                            {(sectionData.items || []).map((item, i) => (
                              <div key={i} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                                <div className="flex-1 space-y-2">
                                  <input
                                    type="text"
                                    value={item.title || ""}
                                    onChange={(e) => updateArrayItem("services", "items", i, "title", e.target.value)}
                                    placeholder="Titel"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                  <textarea
                                    value={item.description || ""}
                                    onChange={(e) => updateArrayItem("services", "items", i, "description", e.target.value)}
                                    placeholder="Beskrivning"
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                                <button
                                  onClick={() => removeArrayItem("services", "items", i)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addArrayItem("services", "items", { title: "", description: "", icon: "globe" })}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"
                            >
                              <Plus className="w-4 h-4" />
                              Lägg till tjänst
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Contact Section */}
                    {section.key === "contact" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input
                            type="text"
                            value={sectionData.title || ""}
                            onChange={(e) => updateField("contact", "title", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">E-post</label>
                          <input
                            type="email"
                            value={sectionData.email || ""}
                            onChange={(e) => updateField("contact", "email", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                          <input
                            type="text"
                            value={sectionData.phone || ""}
                            onChange={(e) => updateField("contact", "phone", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                      </>
                    )}

                    {/* Generic fields for other sections */}
                    {["cta"].includes(section.key) && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input
                            type="text"
                            value={sectionData.title || ""}
                            onChange={(e) => updateField(section.key, "title", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Undertitel</label>
                          <textarea
                            value={sectionData.subtitle || sectionData.description || ""}
                            onChange={(e) => updateField(section.key, "subtitle", e.target.value)}
                            rows={2}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Primär CTA text</label>
                            <input
                              type="text"
                              value={sectionData.cta_primary?.text || ""}
                              onChange={(e) => updateNestedField(section.key, "cta_primary", "text", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Primär CTA länk</label>
                            <input
                              type="text"
                              value={sectionData.cta_primary?.link || ""}
                              onChange={(e) => updateNestedField(section.key, "cta_primary", "link", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sekundär CTA text</label>
                            <input
                              type="text"
                              value={sectionData.cta_secondary?.text || ""}
                              onChange={(e) => updateNestedField(section.key, "cta_secondary", "text", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sekundär CTA länk</label>
                            <input
                              type="text"
                              value={sectionData.cta_secondary?.link || ""}
                              onChange={(e) => updateNestedField(section.key, "cta_secondary", "link", e.target.value)}
                              className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Why Elido Section */}
                    {section.key === "why_elido" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input
                            type="text"
                            value={sectionData.title || ""}
                            onChange={(e) => updateField("why_elido", "title", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Undertitel</label>
                          <input
                            type="text"
                            value={sectionData.subtitle || ""}
                            onChange={(e) => updateField("why_elido", "subtitle", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Fördelar</label>
                          <div className="space-y-3">
                            {(sectionData.items || []).map((item, i) => (
                              <div key={i} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                                <div className="flex-1 space-y-2">
                                  <input
                                    type="text"
                                    value={item.title || ""}
                                    onChange={(e) => updateArrayItem("why_elido", "items", i, "title", e.target.value)}
                                    placeholder="Titel"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                  <textarea
                                    value={item.description || ""}
                                    onChange={(e) => updateArrayItem("why_elido", "items", i, "description", e.target.value)}
                                    placeholder="Beskrivning"
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                                <button
                                  onClick={() => removeArrayItem("why_elido", "items", i)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addArrayItem("why_elido", "items", { title: "", description: "", icon: "zap" })}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"
                            >
                              <Plus className="w-4 h-4" />
                              Lägg till fördel
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Process Section */}
                    {section.key === "process" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input
                            type="text"
                            value={sectionData.title || ""}
                            onChange={(e) => updateField("process", "title", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Undertitel</label>
                          <input
                            type="text"
                            value={sectionData.subtitle || ""}
                            onChange={(e) => updateField("process", "subtitle", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Processteg</label>
                          <div className="space-y-3">
                            {(sectionData.steps || []).map((step, i) => (
                              <div key={i} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                                <div className="w-12 h-12 bg-[#255E69]/20 rounded-lg flex items-center justify-center text-[#255E69] font-bold">
                                  {step.number || `0${i + 1}`}
                                </div>
                                <div className="flex-1 space-y-2">
                                  <input
                                    type="text"
                                    value={step.title || ""}
                                    onChange={(e) => {
                                      const newSteps = [...(sectionData.steps || [])];
                                      newSteps[i] = { ...newSteps[i], title: e.target.value };
                                      updateField("process", "steps", newSteps);
                                    }}
                                    placeholder="Titel"
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                  <textarea
                                    value={step.description || ""}
                                    onChange={(e) => {
                                      const newSteps = [...(sectionData.steps || [])];
                                      newSteps[i] = { ...newSteps[i], description: e.target.value };
                                      updateField("process", "steps", newSteps);
                                    }}
                                    placeholder="Beskrivning"
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    const newSteps = sectionData.steps.filter((_, idx) => idx !== i);
                                    updateField("process", "steps", newSteps);
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg h-fit"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const newSteps = [...(sectionData.steps || []), { 
                                  number: `0${(sectionData.steps?.length || 0) + 1}`, 
                                  title: "", 
                                  description: "" 
                                }];
                                updateField("process", "steps", newSteps);
                              }}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-[#255E69] hover:bg-[#255E69]/5 rounded-lg"
                            >
                              <Plus className="w-4 h-4" />
                              Lägg till steg
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* More Than Websites Section */}
                    {section.key === "more_than_websites" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input
                            type="text"
                            value={sectionData.title || ""}
                            onChange={(e) => updateField("more_than_websites", "title", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Beskrivning</label>
                          <textarea
                            value={sectionData.description || ""}
                            onChange={(e) => updateField("more_than_websites", "description", e.target.value)}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Höjdpunkter (en per rad)</label>
                          <textarea
                            value={(sectionData.highlights || []).join("\n")}
                            onChange={(e) => updateField("more_than_websites", "highlights", e.target.value.split("\n").filter(h => h.trim()))}
                            rows={5}
                            placeholder="AI-driven kundservice och chatbots&#10;Automatiserade arbetsflöden&#10;..."
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg font-mono text-sm"
                          />
                        </div>
                      </>
                    )}

                    {/* SEO Links Section */}
                    {section.key === "seo_links" && (
                      <>
                        {/* Import button if no links exist */}
                        {(!sectionData.links || sectionData.links.length === 0) && (
                          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <p className="text-sm text-emerald-700 mb-3">
                              Inga SEO-länkar finns ännu. Importera standardinnehåll för att komma igång snabbt.
                            </p>
                            <button
                              onClick={seedSeoLinks}
                              disabled={seeding}
                              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                            >
                              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                              Importera SEO-länkar
                            </button>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                          <input
                            type="text"
                            value={sectionData.title || ""}
                            onChange={(e) => updateField("seo_links", "title", e.target.value)}
                            placeholder="Populära tjänster"
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Undertitel</label>
                          <input
                            type="text"
                            value={sectionData.subtitle || ""}
                            onChange={(e) => updateField("seo_links", "subtitle", e.target.value)}
                            placeholder="Utforska våra specialområden..."
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">SEO-beskrivning</label>
                          <textarea
                            value={sectionData.description || ""}
                            onChange={(e) => updateField("seo_links", "description", e.target.value)}
                            rows={4}
                            placeholder="Längre text om företaget för SEO..."
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                          <p className="mt-1 text-xs text-gray-500">Denna text visas på startsidan och hjälper med SEO.</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">SEO-länkar</label>
                          <div className="space-y-3">
                            {(sectionData.links || []).map((link, linkIndex) => (
                              <div key={linkIndex} className="p-4 bg-gray-50 rounded-lg space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-700">Länk {linkIndex + 1}</span>
                                  <button
                                    onClick={() => {
                                      const newLinks = [...(sectionData.links || [])];
                                      newLinks.splice(linkIndex, 1);
                                      updateField("seo_links", "links", newLinks);
                                    }}
                                    className="text-red-500 hover:text-red-700 text-sm"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={link.title || ""}
                                  onChange={(e) => {
                                    const newLinks = [...(sectionData.links || [])];
                                    newLinks[linkIndex] = { ...newLinks[linkIndex], title: e.target.value };
                                    updateField("seo_links", "links", newLinks);
                                  }}
                                  placeholder="Titel (t.ex. Hemsida för småföretag)"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                                <input
                                  type="text"
                                  value={link.url || ""}
                                  onChange={(e) => {
                                    const newLinks = [...(sectionData.links || [])];
                                    newLinks[linkIndex] = { ...newLinks[linkIndex], url: e.target.value };
                                    updateField("seo_links", "links", newLinks);
                                  }}
                                  placeholder="URL (t.ex. /hemsida-smaforetag)"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                                <input
                                  type="text"
                                  value={link.description || ""}
                                  onChange={(e) => {
                                    const newLinks = [...(sectionData.links || [])];
                                    newLinks[linkIndex] = { ...newLinks[linkIndex], description: e.target.value };
                                    updateField("seo_links", "links", newLinks);
                                  }}
                                  placeholder="Kort beskrivning"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                                />
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                const newLinks = [...(sectionData.links || []), { title: "", url: "", description: "" }];
                                updateField("seo_links", "links", newLinks);
                              }}
                              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-[#255E69] hover:text-[#255E69] w-full justify-center"
                            >
                              <Plus className="w-4 h-4" />
                              Lägg till länk
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Footer Section */}
                    {section.key === "footer" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Företagsnamn</label>
                          <input
                            type="text"
                            value={sectionData.company_name || ""}
                            onChange={(e) => updateField("footer", "company_name", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
                          <input
                            type="text"
                            value={sectionData.tagline || ""}
                            onChange={(e) => updateField("footer", "tagline", e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Copyright-text</label>
                          <input
                            type="text"
                            value={sectionData.copyright || ""}
                            onChange={(e) => updateField("footer", "copyright", e.target.value)}
                            placeholder="© {year} Elido AB. Alla rättigheter förbehållna."
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                          <p className="mt-1 text-xs text-gray-500">Använd {"{year}"} för att automatiskt visa aktuellt år.</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Plattformstext (branding)</label>
                          <input
                            type="text"
                            value={sectionData.platform_branding || ""}
                            onChange={(e) => updateField("footer", "platform_branding", e.target.value)}
                            placeholder="Egenutvecklad plattform optimerad för att generera fler kunder"
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                          />
                          <p className="mt-1 text-xs text-gray-500">Visas i footer och vid prissektionen. Lämna tomt för att dölja.</p>
                        </div>
                      </>
                    )}

                    {/* Save Button */}
                    <div className="pt-4">
                      <button
                        onClick={() => saveSection(section.key)}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-[#255E69] text-white rounded-lg text-sm font-medium hover:bg-[#1e4a53] disabled:opacity-50"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Spara {section.label.toLowerCase()}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
      )}
    </div>
  );
}
